import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiWifiOff } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SectionCard from '../../components/ui/SectionCard';
import LoadingScreen from '../../components/ui/LoadingScreen';
import DynamicQuestionField from '../../components/survey/DynamicQuestionField';
import { getSurveyBySlug } from '../../services/dynamicFormService';
import { buildQuestionsByKey, isQuestionVisible, validateVisibleQuestions } from '../../utils/dynamicForm';
import {
  buildHousePayload,
  enqueueHouse,
  syncQueuedHouses,
  getQueuedHousesCount,
  HouseQueueFullError,
  MAX_QUEUED_HOUSES,
} from '../../services/houseCaptureService';

const FORM_SLUG = 'house-registration';

/**
 * Registro de vivienda: formulario dinámico por `slug`
 * (`GET /v1/surveys/house-registration`), de una sola página (sin
 * pasos), con preguntas condicionales resueltas por `question_key`.
 *
 * El envío (`POST /v1/houses`) es offline-first: el registro se guarda
 * primero en una cola local (`houseCaptureService`, máx.
 * {@link MAX_QUEUED_HOUSES} sin sincronizar) y luego se intenta
 * sincronizar en segundo plano. La ubicación GPS está deshabilitada
 * por ahora y se envía siempre en `0`.
 */
export default function HouseRegistrationView() {
  const [survey, setSurvey] = useState(undefined); // undefined=cargando, null=no encontrado
  const [loadError, setLoadError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState('idle'); // idle | saving | done
  const [submitError, setSubmitError] = useState(null);
  const [syncState, setSyncState] = useState('idle'); // idle | syncing | synced | error
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let active = true;
    setSurvey(undefined);
    setLoadError(null);
    setOffline(!navigator.onLine);
    setAnswers({});
    setErrors({});
    setSubmitState('idle');
    setSubmitError(null);

    getSurveyBySlug(FORM_SLUG)
      .then((data) => {
        if (!active) return;
        setSurvey(data);
        if (!navigator.onLine) setOffline(true);
      })
      .catch((err) => {
        if (!active) return;
        setSurvey(null);
        setLoadError(err.message || 'No se pudo cargar el formulario.');
      });

    return () => {
      active = false;
    };
  }, []);

  const questions = useMemo(() => survey?.questions ?? [], [survey]);
  const questionsByKey = useMemo(() => buildQuestionsByKey(questions), [questions]);
  const visibleQuestions = useMemo(
    () => questions.filter((q) => isQuestionVisible(q, answers, questionsByKey)),
    [questions, answers, questionsByKey]
  );

  if (survey === undefined) {
    return <LoadingScreen label="Cargando formulario…" />;
  }

  function handleAnswerChange(question, value) {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    setErrors((prev) => {
      if (!prev[question.id]) return prev;
      const next = { ...prev };
      delete next[question.id];
      return next;
    });
  }

  async function handleSubmit() {
    const validationErrors = validateVisibleQuestions(visibleQuestions, answers);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitState('saving');
    setSubmitError(null);
    setSyncState('idle');
    try {
      const answersByKey = questions.reduce((acc, question) => {
        if (visibleQuestions.includes(question)) {
          acc[question.key] = answers[question.id] ?? null;
        }
        return acc;
      }, {});
      const house = buildHousePayload({ answersByKey });
      enqueueHouse(house);
      setSubmitState('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      attemptBackgroundSync();
    } catch (err) {
      setSubmitState('idle');
      setSubmitError(
        err instanceof HouseQueueFullError
          ? err.message
          : err.message || 'No se pudo guardar el registro en este dispositivo.'
      );
    }
  }

  async function attemptBackgroundSync() {
    setSyncState('syncing');
    try {
      await syncQueuedHouses();
      setSyncState('synced');
    } catch {
      setSyncState('error');
    } finally {
      setPendingCount(getQueuedHousesCount());
    }
  }

  if (!survey) {
    return (
      <DashboardLayout title="Registro de vivienda" subtitle="No encontrado">
        {loadError && (
          <div className="form-error" role="alert">
            <FiAlertCircle /> {loadError}
          </div>
        )}
        <SectionCard>
          <p>El formulario solicitado no existe o fue eliminado.</p>
        </SectionCard>
        <Link to="/data-entry" className="btn btn-outline btn-block">
          <FiArrowLeft /> Volver al inicio
        </Link>
      </DashboardLayout>
    );
  }

  if (submitState === 'done') {
    return (
      <DashboardLayout title="¡Listo!" subtitle={survey.label}>
        <SectionCard>
          <div className="survey-success">
            <FiCheckCircle className="survey-success-icon" />
            <p className="empty-state__title">Registro guardado</p>
            <p className="empty-state__description">
              {syncState === 'synced' && 'La información ya se sincronizó con el servidor.'}
              {syncState === 'syncing' && 'Sincronizando con el servidor…'}
              {syncState === 'error' &&
                `Se guardó en este dispositivo. Aún no hay conexión para sincronizar (${pendingCount}/${MAX_QUEUED_HOUSES} pendientes).`}
              {syncState === 'idle' && 'La información quedó guardada en este dispositivo.'}
            </p>
          </div>
        </SectionCard>
        <Link to="/data-entry" className="btn btn-primary btn-block">
          Volver al inicio
        </Link>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={survey.label} subtitle={survey.description}>
      {offline && (
        <div className="form-error form-error--muted" role="status">
          <FiWifiOff /> Sin conexión: mostrando una copia guardada de este formulario.
        </div>
      )}

      {submitError && (
        <div className="form-error" role="alert">
          <FiAlertCircle /> {submitError}
        </div>
      )}

      {questions.length === 0 ? (
        <SectionCard>
          <p>Este formulario todavía no tiene preguntas configuradas.</p>
        </SectionCard>
      ) : (
        <SectionCard>
          {visibleQuestions.map((question) => (
            <DynamicQuestionField
              key={question.id}
              question={question}
              value={answers[question.id]}
              error={errors[question.id]}
              onChange={(value) => handleAnswerChange(question, value)}
            />
          ))}
        </SectionCard>
      )}

      <div className="survey-nav">
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={handleSubmit}
          disabled={submitState === 'saving'}
        >
          {submitState === 'saving' ? 'Guardando…' : (
            <>
              <FiCheckCircle /> Guardar registro
            </>
          )}
        </button>
      </div>
    </DashboardLayout>
  );
}
